import { createContext, use, useMemo, type ReactNode } from 'react';

import { useCompanyID } from '@/hooks/useCompanyID';
import { withCompanyHeader, type CompanyHeaderOptions } from '@/lib/http/request';

// The tenant on screen, which is not the company the user acts as under chain access (ADR-0025); `null` means no override, so call sites outside a provider read the store as before.
const ViewedCompanyContext = createContext<string | null>(null);

interface ViewedCompanyProviderProps {
	companyId: string;
	children: ReactNode;
}

export function ViewedCompanyProvider({ companyId, children }: ViewedCompanyProviderProps) {
	return <ViewedCompanyContext value={companyId}>{children}</ViewedCompanyContext>;
}

export function useViewedCompanyID(): string {
	const viewed = use(ViewedCompanyContext);
	const selected = useCompanyID();

	return viewed ?? selected;
}

export function useViewedCompanyScope(): Partial<CompanyHeaderOptions> {
	const viewed = use(ViewedCompanyContext);

	return useMemo(() => (viewed === null ? {} : withCompanyHeader({ companyId: viewed })), [viewed]);
}

// Acting-company affordances hang off this: routes and writes resolve against the selected company, so they are wrong wherever a foreign tenant is in view.
export function useIsViewingOwnCompany(): boolean {
	const viewed = use(ViewedCompanyContext);
	const selected = useCompanyID();

	return viewed === null || viewed === selected;
}
